import * as echarts from 'echarts';

// Reusable chart configuration for time series plots
interface ChartConfigOptions {
  isDark: boolean;
  axisColor: string;
  sortedModels?: string[];
  variable: {
    displayUnit?: string;
    unit?: string;
    label?: string;
  };
  yAxisRange: {
    min: number;
    max: number;
  };
  series: any[];
  formatInUserTimezone: (date: string | Date, format: string) => string;
  isFullscreen?: boolean;
  showDataZoom?: boolean;
  showLegend?: boolean;
  observationName?: string;
  yAxisInterval?: number;
}

export function buildTimeSeriesChartConfig({
  isDark,
  axisColor,
  sortedModels = [],
  variable,
  yAxisRange,
  series,
  formatInUserTimezone,
  isFullscreen = false,
  showDataZoom = true,
  showLegend = true,
  observationName,
  yAxisInterval: customYAxisInterval,
}: ChartConfigOptions): echarts.EChartsOption {
  // Calculate grid top based on legend layout (more space needed for multi-row legend)
  const hasObservationSeparate = observationName && sortedModels.length > 1;
  const legendTopSpace = hasObservationSeparate
    ? isFullscreen
      ? '25%'
      : '24%'
    : isFullscreen
      ? '12%'
      : '6%';

  const gridConfig = isFullscreen
    ? {
        left: '3%',
        right: '3%',
        bottom: showDataZoom ? '8%' : '5%',
        top: showLegend ? legendTopSpace : '8%',
        containLabel: true,
      }
    : {
        left: '8%',
        right: '8%',
        bottom: showDataZoom ? '12%' : '8%',
        top: showLegend ? legendTopSpace : '3%',
        containLabel: true,
      };

  const displayUnit = variable.displayUnit || variable.unit || '';

  // Determine y-axis interval based on variable type
  const isTemperature = displayUnit.includes('°C') || displayUnit.includes('K');
  const isWindSpeed = displayUnit.includes('m/s');
  const yAxisInterval =
    customYAxisInterval !== undefined
      ? customYAxisInterval
      : isTemperature
        ? 5
        : isWindSpeed
          ? 4
          : undefined;

  return {
    backgroundColor: 'transparent',
    grid: gridConfig,
    tooltip: {
      trigger: 'axis',
      backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
      borderColor: isDark ? '#374151' : '#E5E7EB',
      borderWidth: 1,
      borderRadius: 8,
      padding: [12, 16],
      textStyle: {
        color: isDark ? '#F9FAFB' : '#111827',
        fontSize: 12,
      },
      formatter: (params: any) => {
        if (!params || params.length === 0) return '';
        const time = formatInUserTimezone(params[0].value[0], 'MMM dd, HH:mm');
        let result = `<div style="font-size: 12px; font-weight: 600; margin-bottom: 8px; color: ${isDark ? '#F3F4F6' : '#1F2937'};">${time}</div>`;
        params.forEach((param: any) => {
          if (param.value[1] !== '-' && param.value[1] !== undefined && param.value[1] !== null) {
            const value =
              typeof param.value[1] === 'number' ? param.value[1].toFixed(1) : param.value[1];
            result += `<div style="display: flex; align-items: center; margin-bottom: 4px;">
              <span style="display: inline-block; width: 12px; height: 12px; border-radius: 50%; background: ${param.color}; margin-right: 12px;"></span>
              <span style="color: ${isDark ? '#E5E7EB' : '#374151'};">${param.seriesName}: </span>
              <span style="font-weight: 600; margin-left: 4px; color: ${isDark ? '#F9FAFB' : '#111827'};">${value} ${displayUnit}</span>
            </div>`;
          }
        });
        return result;
      },
    },
    legend: showLegend
      ? observationName && sortedModels.length > 1
        ? [
            // First row: Observations (centered)
            {
              data: [observationName],
              textStyle: {
                color: axisColor,
                fontSize: 12,
                fontWeight: 500,
              },
              top: isFullscreen ? '1.5%' : '1%',
              itemGap: 20,
              itemWidth: 25,
              itemHeight: 14,
              selectedMode: true,
              left: 'center',
            },
            // Second row: Forecasts (centered)
            {
              data: sortedModels.filter((m) => m !== observationName),
              textStyle: {
                color: axisColor,
                fontSize: 12,
                fontWeight: 500,
              },
              top: isFullscreen ? '9.5%' : '10%',
              itemGap: 20,
              itemWidth: 25,
              itemHeight: 14,
              selectedMode: true,
              left: 'center',
            },
          ]
        : {
            data: sortedModels,
            textStyle: {
              color: axisColor,
              fontSize: 12,
              fontWeight: 500,
            },
            top: isFullscreen ? '2%' : '1%',
            itemGap: 20,
            itemWidth: 25,
            itemHeight: 14,
            selectedMode: true,
          }
      : undefined,
    dataZoom: showDataZoom
      ? [
          {
            type: 'slider' as const,
            show: true,
            xAxisIndex: [0],
            bottom: '2%',
            height: 16,
            borderColor: 'transparent',
            backgroundColor: isDark ? 'rgba(55, 65, 81, 0.3)' : 'rgba(243, 244, 246, 0.8)',
            fillerColor: isDark ? 'rgba(236, 72, 153, 0.3)' : 'rgba(236, 72, 153, 0.2)',
            textStyle: {
              color: axisColor,
              fontSize: 10,
            },
            handleStyle: {
              color: '#EC4899',
              borderColor: '#EC4899',
              borderWidth: 1,
            },
            moveHandleStyle: {
              color: isDark ? '#6B7280' : '#9CA3AF',
            },
            selectedDataBackground: {
              lineStyle: { color: 'transparent' },
              areaStyle: { color: 'transparent' },
            },
            dataBackground: {
              lineStyle: { color: 'transparent' },
              areaStyle: { color: 'transparent' },
            },
            start: 0,
            end: 100,
            filterMode: 'none' as const,
          },
          {
            type: 'inside' as const,
            xAxisIndex: [0],
            zoomOnMouseWheel: true,
            moveOnMouseMove: true,
            filterMode: 'none' as const,
          },
        ]
      : undefined,
    xAxis: {
      type: 'time',
      axisLabel: {
        formatter: (value: number) => {
          const date = new Date(value);
          return formatInUserTimezone(date.toISOString(), 'MMM dd');
        },
        color: axisColor,
        fontSize: 11,
        fontWeight: 500,
        hideOverlap: true,
        rotate: 0,
        margin: 12,
      },
      splitLine: {
        show: false,
      },
      axisLine: {
        lineStyle: {
          color: axisColor,
        },
      },
    },
    yAxis: {
      type: 'value',
      name: displayUnit,
      nameLocation: 'middle',
      nameGap: 40,
      nameTextStyle: {
        color: axisColor,
        fontSize: 12,
        fontWeight: 500,
        padding: [0, 0, 0, 20],
      },
      min: yAxisRange.min,
      max: yAxisRange.max,
      interval: yAxisInterval,
      axisLabel: {
        color: axisColor,
        fontSize: 11,
        fontWeight: 500,
        formatter: (value: number) => {
          if (Math.abs(value) >= 1000) {
            return (value / 1000).toFixed(1) + 'k';
          }
          return value.toFixed(1);
        },
      },
      splitLine: {
        lineStyle: {
          color: isDark ? '#374151' : '#F3F4F6',
          type: 'solid',
          width: 1,
        },
      },
      axisLine: {
        lineStyle: {
          color: axisColor,
        },
      },
    },
    series,
  };
}
